// ** react imports
import { useState, useMemo, useEffect } from 'react';

// ** api methods for tax file management
import {
  useGetAllTaxFilesQuery,
  useGetTaxFileStatsQuery,
  useGetTaxFileQuery,
  useGetTaxFileByCustomerIdQuery,
  useUpdateAnswerMutation,
  useGetTaxFileHistoryQuery,
  useAssignTaxFileMutation,
  useAddNotesMutation,
  useGetQuizResponseByTaxFileIdQuery,
  useAddTaxFileHistoryMutation,
} from '../../../redux/slices/taxFiles/slice';

export const useGetAllTaxFiles = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [query, setQuery] = useState({});

    // api call query
    const {
      data: getAllTaxFiles,
      isLoading: isGetAllTaxFilesLoading,
      isError: isGetAllTaxFilesError,
      error: getAllTaxFilesError,
      isFetching: isGetAllTaxFilesFetching,
      refetch: refetchGetAllTaxFiles,
    } = useGetAllTaxFilesQuery(query, {
      skip: skipApi,
    });

    // handlers
    const handleGetAllTaxFiles = async query => {
      setSkipApi(false);
      setQuery(query);
    };

    // effect
    useEffect(() => {
      if (getAllTaxFiles !== undefined) {
        // pagination implementation was pending.
        setData({
          data: getAllTaxFiles.data.data,
          total: getAllTaxFiles.data.total,
        });
      }
    }, [getAllTaxFiles]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetAllTaxFilesLoading,
        isGetAllTaxFilesError,
        getAllTaxFilesError,
        isGetAllTaxFilesFetching,
        refetchGetAllTaxFiles,
        getAllTaxFiles: handleGetAllTaxFiles,
      }),
      [
        data,
        isGetAllTaxFilesLoading,
        isGetAllTaxFilesError,
        getAllTaxFilesError,
        isGetAllTaxFilesFetching,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching all tax files:', error);
    throw error;
  }
};

export const useGetTaxFileStats = () => {
  try {
    // states
    const [data, setData] = useState({});

    // api call query
    const {
      data: getTaxFileStats,
      isLoading: isGetTaxFileStatsLoading,
      isError: isGetTaxFileStatsError,
      error: getTaxFileStatsError,
      refetch: refetchGetTaxFileStats,
    } = useGetTaxFileStatsQuery();

    // effect
    useEffect(() => {
      if (getTaxFileStats !== undefined) {
        setData(getTaxFileStats.data);
      }
    }, [getTaxFileStats]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetTaxFileStatsLoading,
        isGetTaxFileStatsError,
        getTaxFileStatsError,
        refetchGetTaxFileStats,
      }),
      [
        data,
        isGetTaxFileStatsLoading,
        isGetTaxFileStatsError,
        getTaxFileStatsError,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching tax file stats:', error);
    throw error;
  }
};

export const useGetTaxFile = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [taxFileId, setTaxFileId] = useState('');

    // api call query
    const {
      data: getTaxFile,
      isLoading: isGetTaxFileLoading,
      isError: isGetTaxFileError,
      error: getTaxFileError,
      refetch: refetchGetTaxFile,
    } = useGetTaxFileQuery(taxFileId, { skip: skipApi });

    // handlers
    const handleGetTaxFile = async taxFileId => {
      setSkipApi(false);
      setTaxFileId(taxFileId);
    };

    // effect
    useEffect(() => {
      if (getTaxFile !== undefined) {
        setData(getTaxFile.data);
      }
    }, [getTaxFile]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetTaxFileLoading,
        isGetTaxFileError,
        getTaxFileError,
        refetchGetTaxFile,
        getTaxFile: handleGetTaxFile,
      }),
      [data, isGetTaxFileLoading, isGetTaxFileError, getTaxFileError]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching tax file:', error);
    throw error;
  }
};

export const useGetTaxFileByCustomerId = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [queryParams, setQueryParams] = useState({});

    // api call query
    const {
      data: getTaxFileByCustomerId,
      isLoading: isGetTaxFileByCustomerIdLoading,
      isError: isGetTaxFileByCustomerIdError,
      error: getTaxFileByCustomerIdError,
      isFetching: isGetTaxFileByCustomerIdFetching,
      refetch: refetchGetTaxFileByCustomerId,
    } = useGetTaxFileByCustomerIdQuery(queryParams, { skip: skipApi });

    // handlers
    const handleGetTaxFileByCustomerId = async (
      customerId,
      additionalQuery = {}
    ) => {
      setSkipApi(false);
      // Filter out null/undefined values from query before sending to API
      const cleanQuery = Object.fromEntries(
        Object.entries(additionalQuery).filter(
          ([_, value]) => value !== null && value !== undefined && value !== ''
        )
      );
      setQueryParams({ customerId, ...cleanQuery });
    };

    // effect
    useEffect(() => {
      if (getTaxFileByCustomerId !== undefined) {
        // Handle both single tax file and array of tax files
        if (Array.isArray(getTaxFileByCustomerId.data)) {
          setData({
            data: getTaxFileByCustomerId.data,
            total:
              getTaxFileByCustomerId.total ||
              getTaxFileByCustomerId.data.length,
          });
        } else {
          setData(getTaxFileByCustomerId.data);
        }
      }
    }, [getTaxFileByCustomerId]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetTaxFileByCustomerIdLoading,
        isGetTaxFileByCustomerIdError,
        getTaxFileByCustomerIdError,
        isGetTaxFileByCustomerIdFetching,
        refetchGetTaxFileByCustomerId,
        getTaxFileByCustomerId: handleGetTaxFileByCustomerId,
      }),
      [
        data,
        isGetTaxFileByCustomerIdLoading,
        isGetTaxFileByCustomerIdError,
        getTaxFileByCustomerIdError,
        isGetTaxFileByCustomerIdFetching,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching tax file by customer ID:', error);
    throw error;
  }
};

export const useUpdateAnswer = () => {
  try {
    // api call mutation
    const [
      updateAnswer,
      {
        isLoading: isUpdateAnswerLoading,
        isError: isUpdateAnswerError,
        error: updateAnswerError,
      },
    ] = useUpdateAnswerMutation();

    // handlers
    const handleUpdateAnswer = async (taxFileId, questionId, answer) => {
      return await updateAnswer({ taxFileId, questionId, answer });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isUpdateAnswerLoading,
        isUpdateAnswerError,
        updateAnswerError,
        updateAnswer: handleUpdateAnswer,
      }),
      [isUpdateAnswerLoading, isUpdateAnswerError, updateAnswerError]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error updating answer:', error);
    throw error;
  }
};

export const useGetTaxFileHistory = () => {
  try {
    // states
    const [data, setData] = useState([]);
    const [skipApi, setSkipApi] = useState(true);
    const [query, setQuery] = useState({});

    // api call query
    const {
      data: getTaxFileHistory,
      isLoading: isGetTaxFileHistoryLoading,
      isError: isGetTaxFileHistoryError,
      error: getTaxFileHistoryError,
      isFetching: isGetTaxFileHistoryFetching,
      refetch: refetchGetTaxFileHistory,
    } = useGetTaxFileHistoryQuery(query, {
      skip: skipApi,
    });

    // handlers
    const handleGetTaxFileHistory = async (taxFileId, additionalQuery = {}) => {
      setSkipApi(false);
      setQuery({ taxFileId, ...additionalQuery });
    };

    // effect
    useEffect(() => {
      if (getTaxFileHistory !== undefined) {
        setData(getTaxFileHistory.data);
        setSkipApi(true);
      }
    }, [getTaxFileHistory]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetTaxFileHistoryLoading,
        isGetTaxFileHistoryError,
        getTaxFileHistoryError,
        isGetTaxFileHistoryFetching,
        refetchGetTaxFileHistory,
        getTaxFileHistory: handleGetTaxFileHistory,
      }),
      [
        data,
        isGetTaxFileHistoryLoading,
        isGetTaxFileHistoryError,
        getTaxFileHistoryError,
        isGetTaxFileHistoryFetching,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching tax file history:', error);
    throw error;
  }
};

export const useAssignTaxFile = () => {
  try {
    // api call mutation
    const [
      assignTaxFile,
      {
        isLoading: isAssignTaxFileLoading,
        isError: isAssignTaxFileError,
        error: assignTaxFileError,
      },
    ] = useAssignTaxFileMutation();

    // handlers
    const handleAssignTaxFile = async (taxFileId, data) => {
      return await assignTaxFile({ taxFileId, data });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isAssignTaxFileLoading,
        isAssignTaxFileError,
        assignTaxFileError,
        assignTaxFile: handleAssignTaxFile,
      }),
      [isAssignTaxFileLoading, isAssignTaxFileError, assignTaxFileError]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error assigning tax file:', error);
    throw error;
  }
};

export const useAddNotes = () => {
  try {
    // api call mutation
    const [
      addNotes,
      {
        isLoading: isAddNotesLoading,
        isError: isAddNotesError,
        error: addNotesError,
      },
    ] = useAddNotesMutation();

    // handlers
    const handleAddNotes = async (taxFileId, data) => {
      return await addNotes({ taxFileId, data });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isAddNotesLoading,
        isAddNotesError,
        addNotesError,
        addNotes: handleAddNotes,
      }),
      [isAddNotesLoading, isAddNotesError, addNotesError]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error adding notes:', error);
    throw error;
  }
};

export const useGetQuizResponseByTaxFileId = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [taxFileId, setTaxFileId] = useState('');

    // api call query
    const {
      data: getQuizResponseByTaxFileId,
      isLoading: isGetQuizResponseByTaxFileIdLoading,
      isError: isGetQuizResponseByTaxFileIdError,
      isFetching: isGetQuizResponseByTaxFileIdFetching,
      error: getQuizResponseByTaxFileIdError,
      refetch: refetchGetQuizResponseByTaxFileId,
    } = useGetQuizResponseByTaxFileIdQuery(taxFileId, { skip: skipApi });

    // handlers
    const handleGetQuizResponseByTaxFileId = async taxFileId => {
      setSkipApi(false);
      setTaxFileId(taxFileId);
    };

    // effect
    useEffect(() => {
      if (getQuizResponseByTaxFileId !== undefined) {
        setData(getQuizResponseByTaxFileId.data);
      }
    }, [getQuizResponseByTaxFileId]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetQuizResponseByTaxFileIdLoading,
        isGetQuizResponseByTaxFileIdError,
        isGetQuizResponseByTaxFileIdFetching,
        getQuizResponseByTaxFileIdError,
        refetchGetQuizResponseByTaxFileId,
        getQuizResponseByTaxFileId: handleGetQuizResponseByTaxFileId,
      }),
      [
        data,
        isGetQuizResponseByTaxFileIdLoading,
        isGetQuizResponseByTaxFileIdError,
        isGetQuizResponseByTaxFileIdFetching,
        getQuizResponseByTaxFileIdError,
        refetchGetQuizResponseByTaxFileId,
        handleGetQuizResponseByTaxFileId,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching quiz response by tax file ID:', error);
    throw error;
  }
};

export const useAddTaxFileHistory = () => {
  try {
    // api call mutation
    const [
      addTaxFileHistory,
      {
        isLoading: isAddTaxFileHistoryLoading,
        isError: isAddTaxFileHistoryError,
        error: addTaxFileHistoryError,
      },
    ] = useAddTaxFileHistoryMutation();

    // handlers
    const handleAddTaxFileHistory = async (taxFileId, data) => {
      return await addTaxFileHistory({ taxFileId, data });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isAddTaxFileHistoryLoading,
        isAddTaxFileHistoryError,
        addTaxFileHistoryError,
        addTaxFileHistory: handleAddTaxFileHistory,
      }),
      [
        isAddTaxFileHistoryLoading,
        isAddTaxFileHistoryError,
        addTaxFileHistoryError,
        handleAddTaxFileHistory,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error adding tax file history:', error);
    throw error;
  }
};
